import { Tabs } from 'antd'
import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS } from 'common/constants/defaultTableParamsConstants'
import { useAppSelector } from 'common/hooks/redux'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import type {
  IntakeTemplateItem,
  SignedDocumentApiItem,
} from 'features/Procedure/state/procedure-api.type'
import {
  useFetchSignedDocumentsQuery,
  useFetchIntakeTemplatesQuery,
} from 'features/Procedure/state/procedure.api'
import { DocTemplatePreviewDialog } from 'features/Procedure/features/treatments/components/doc-template-preview-dialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { formsTableColumns } from './columns'
import { intakeTemplatesColumns } from './intake-columns'
import { FormsPreview } from './preview'
import type { FormsNavigationRequest, FormsSubTabKey } from './signed-documents.types'
import styles from './signed-documents.module.scss'

type SignedDocumentsProps = {
  selectedPatientId: number | null
  navigationRequest?: FormsNavigationRequest | null
  disableActions?: boolean
}

export const SignedDocuments = ({
  selectedPatientId,
  navigationRequest,
  disableActions = false,
}: SignedDocumentsProps) => {
  const selectedFormDocId = useAppSelector((state) => state.procedureReducer.selectedFormDocId)
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )
  const { handleSetSelectedFormDocId } = useProcedureActions()

  const [columns, setColumns] = useState<ObjectType[]>([])
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })

  const [intakeTableParams, setIntakeTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS,
  })
  const [signingTemplate, setSigningTemplate] = useState<IntakeTemplateItem | null>(null)
  const [activeSubTab, setActiveSubTab] = useState<FormsSubTabKey>('pending')

  const shouldSkip = !selectedPatientId

  const {
    data: signedDocuments = [],
    isFetching,
    isLoading,
  } = useFetchSignedDocumentsQuery({ patientId: selectedPatientId! }, { skip: shouldSkip })

  const {
    data: intakeTemplates = [],
    isFetching: isFetchingIntake,
    isLoading: isLoadingIntake,
  } = useFetchIntakeTemplatesQuery({ patientId: selectedPatientId! }, { skip: shouldSkip })

  const handleSelectForm = useCallback(
    (doc: SignedDocumentApiItem) => {
      handleSetSelectedFormDocId(doc.id)
    },
    [handleSetSelectedFormDocId],
  )

  useEffect(() => {
    if (signedDocuments.length > 0 && !selectedFormDocId) {
      handleSetSelectedFormDocId(signedDocuments[0].id)
    } else if (signedDocuments.length === 0) {
      handleSetSelectedFormDocId(null)
    }
  }, [signedDocuments, selectedFormDocId, handleSetSelectedFormDocId])

  useEffect(() => {
    if (selectedFormDocId && signedDocuments.length > 0) {
      const stillExists = signedDocuments.find((doc) => doc.id === selectedFormDocId)
      if (!stillExists && signedDocuments.length > 0) {
        handleSetSelectedFormDocId(signedDocuments[0].id)
      }
    }
  }, [signedDocuments, selectedFormDocId, handleSetSelectedFormDocId])

  useEffect(() => {
    setColumns(
      formsTableColumns({
        selectedFormDocId,
        onSelectForm: handleSelectForm,
      }),
    )
  }, [selectedFormDocId, handleSelectForm])

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    setTableParams((prev) => ({ ...prev, total: signedDocuments.length }))
  }, [signedDocuments.length])

  useEffect(() => {
    setIntakeTableParams((prev) => ({ ...prev, total: intakeTemplates.length }))
  }, [intakeTemplates.length])

  useEffect(() => {
    if (!navigationRequest) return

    setActiveSubTab(navigationRequest.targetSubTab)

    if (navigationRequest.targetSubTab === 'signed') {
      handleSetSelectedFormDocId(navigationRequest.documentId ?? null)
    }
  }, [
    navigationRequest?.requestId,
    navigationRequest?.targetSubTab,
    navigationRequest?.documentId,
    handleSetSelectedFormDocId,
  ])

  const getRowClassName = useCallback(
    (record: SignedDocumentApiItem) => {
      return selectedFormDocId === record.id ? styles.selectedRow : ''
    },
    [selectedFormDocId],
  )

  const handleSignTemplate = useCallback(
    (template: IntakeTemplateItem) => {
      if (disableActions) return
      setSigningTemplate(template)
    },
    [disableActions],
  )

  const handleCloseSigningDialog = useCallback(() => {
    setSigningTemplate(null)
  }, [])

  const intakeCols = useMemo(
    () => intakeTemplatesColumns({ onSign: handleSignTemplate, disableActions }),
    [handleSignTemplate, disableActions],
  )

  return (
    <div className={styles.container}>
      <Tabs
        className={styles.formsTabs}
        activeKey={activeSubTab}
        onChange={(tabKey) => setActiveSubTab(tabKey as FormsSubTabKey)}
        destroyOnHidden
        items={[
          {
            key: 'pending',
            label: 'Pending',
            children: (
              <div className={styles.pendingContent}>
                <div className={styles.pendingTableWrapper}>
                  <Table
                    itemKey='id'
                    tableParams={intakeTableParams}
                    dataSource={intakeTemplates}
                    setTableParams={setIntakeTableParams}
                    columns={intakeCols}
                    isLoading={isFetchingIntake || isLoadingIntake}
                    haveActions={false}
                    pagination={false}
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'signed',
            label: 'Signed',
            children: (
              <div className={styles.signedContent}>
                <div className={styles.signedTableSection}>
                  <Table
                    itemKey='id'
                    tableParams={tableParams}
                    dataSource={signedDocuments}
                    setTableParams={setTableParams}
                    columns={columns}
                    isLoading={isFetching || isLoading}
                    haveActions={false}
                    pagination={false}
                    rowClassName={getRowClassName}
                  />
                </div>
                <div className={styles.signedPreviewSection}>
                  <FormsPreview selectedPatientId={selectedPatientId} />
                </div>
              </div>
            ),
          },
        ]}
      />

      {signingTemplate && selectedPatientId && selectedEncounterUid && (
        <DocTemplatePreviewDialog
          isVisible={Boolean(signingTemplate)}
          onClose={handleCloseSigningDialog}
          patientId={selectedPatientId}
          encounterUid={selectedEncounterUid}
          templateId={signingTemplate.id}
          title={signingTemplate.title}
          onSubmitSuccess={handleCloseSigningDialog}
        />
      )}
    </div>
  )
}
